import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient, { formatApiError, getAuthToken } from '../utils/api';
import useGoogleDrivePicker from '../utils/useGoogleDrivePicker';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Trash, MusicNotes, Folder, LinkBreak, ArrowsClockwise, Warning, Play, Stop, ShieldCheck, WarningCircle } = PhosphorIcons;

const FILE_BASE = process.env.REACT_APP_BACKEND_URL;

const MusicLibrary = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [driveConfig, setDriveConfig] = useState({ configured: false });
  const [pickerBusy, setPickerBusy] = useState(false);
  const [reauthTrackId, setReauthTrackId] = useState(null);
  const [previewTrackId, setPreviewTrackId] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [playbackErrors, setPlaybackErrors] = useState([]);
  const previewAudioRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchData = useCallback(async () => {
    try {
      const [tracksRes, statusRes, cfgRes, errsRes] = await Promise.all([
        apiClient.get('/tracks'),
        apiClient.get('/drive/status').catch(() => ({ data: { connected: false } })),
        apiClient.get('/drive/config').catch(() => ({ data: { configured: false } })),
        apiClient.get('/playback-errors').catch(() => ({ data: [] })),
      ]);
      setTracks(tracksRes.data);
      setDriveStatus(statusRes.data || { connected: false });
      setDriveConfig(cfgRes.data || { configured: false });
      setPlaybackErrors(Array.isArray(errsRes.data) ? errsRes.data : []);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive connected successfully!');
      setSearchParams({}, { replace: true });
    }
    if (searchParams.get('drive_error')) {
      const err = searchParams.get('drive_error');
      if (err === 'session_mismatch') {
        toast.error('Google Drive was initiated for a different account. Please log out of other tenants and try again.');
      } else {
        toast.error('Failed to connect Google Drive. Please try again.');
      }
      setSearchParams({}, { replace: true });
    }
  }, [fetchData, searchParams, setSearchParams]);

  const handleConnectDrive = async () => {
    try {
      const res = await apiClient.get('/drive/connect');
      if (res.data.authorization_url) window.location.href = res.data.authorization_url;
    } catch (error) { toast.error('Google Drive OAuth not configured. Ask admin to set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET.'); }
  };

  const handleDisconnectDrive = async () => {
    if (!window.confirm(
      'Disconnect Google Drive?\n\n' +
      'This will permanently remove ALL content sourced from Drive:\n' +
      '• Tracks imported from your Drive\n' +
      '• Playlists that become empty as a result\n' +
      '• Dayparts & special occasions pointing at those playlists\n' +
      '• Announcements sourced from Drive\n' +
      '• Active playlist on any store using one\n\n' +
      'Local legacy files are not affected. You can reconnect and rebuild any time.'
    )) return;
    try {
      const res = await apiClient.post('/drive/disconnect');
      const {
        tracks_removed = 0, playlists_removed = 0, dayparts_removed = 0,
        special_occasions_removed = 0, stores_cleared = 0, announcements_removed = 0,
      } = res.data || {};
      const parts = [];
      if (tracks_removed) parts.push(`${tracks_removed} track${tracks_removed === 1 ? '' : 's'}`);
      if (playlists_removed) parts.push(`${playlists_removed} playlist${playlists_removed === 1 ? '' : 's'}`);
      if (dayparts_removed) parts.push(`${dayparts_removed} daypart${dayparts_removed === 1 ? '' : 's'}`);
      if (special_occasions_removed) parts.push(`${special_occasions_removed} occasion${special_occasions_removed === 1 ? '' : 's'}`);
      if (announcements_removed) parts.push(`${announcements_removed} announcement${announcements_removed === 1 ? '' : 's'}`);
      if (stores_cleared) parts.push(`${stores_cleared} store${stores_cleared === 1 ? '' : 's'} cleared`);
      toast.success(parts.length ? `Disconnected. Removed ${parts.join(', ')}.` : 'Google Drive disconnected.');
      setDriveStatus({ connected: false });
      await fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  // ── Google Picker integration ─────────────────────────────────────────
  // Two picker instances:
  //   1) "Add new" picker → bulk-import into library
  //   2) "Re-authorize" picker → swap a broken track's file id in place
  const importPicker = useGoogleDrivePicker({
    audioOnly: true,
    multiSelect: true,
    onPicked: async (files) => {
      if (!files.length) return;
      setPickerBusy(true);
      try {
        const res = await apiClient.post('/tracks/add-from-drive', { files });
        const added = res.data?.added?.length || 0;
        const skipped = res.data?.skipped?.length || 0;
        if (added && skipped) toast.success(`${added} track(s) added. ${skipped} already in library.`);
        else if (added) toast.success(`${added} track(s) added to library.`);
        else if (skipped) toast.info(`All ${skipped} selected file(s) are already in your library.`);
        await fetchData();
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setPickerBusy(false); }
    },
  });

  const reauthPicker = useGoogleDrivePicker({
    audioOnly: true,
    multiSelect: false,
    onPicked: async (files) => {
      if (!reauthTrackId || !files.length) return;
      setPickerBusy(true);
      try {
        await apiClient.post(`/tracks/${reauthTrackId}/reauthorize-drive`, files[0]);
        toast.success('Track re-authorized. Playback should resume.');
        await fetchData();
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setPickerBusy(false); setReauthTrackId(null); }
    },
  });

  const openImportPicker = async () => {
    try { await importPicker.openPicker(); }
    catch (e) { toast.error(e.message || 'Could not open Google Picker'); }
  };

  const openReauthPicker = async (trackId) => {
    setReauthTrackId(trackId);
    try { await reauthPicker.openPicker(); }
    catch (e) { toast.error(e.message || 'Could not open Google Picker'); setReauthTrackId(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this track from library?')) return;
    try { await apiClient.delete(`/tracks/${id}`); toast.success('Track removed'); fetchData(); }
    catch (error) { toast.error(formatApiError(error)); }
  };

  // ── Track preview (uses the exact stream URL kiosks use) ───────────────
  const stopPreview = useCallback(() => {
    const a = previewAudioRef.current;
    if (a) {
      try { a.pause(); a.removeAttribute('src'); a.load(); } catch (e) { /* ignore */ }
    }
    setPreviewTrackId(null);
  }, []);

  useEffect(() => () => {
    const a = previewAudioRef.current;
    if (a) { try { a.pause(); } catch (e) { /* ignore */ } }
  }, []);

  const handlePreview = (track) => {
    if (previewTrackId === track.id) { stopPreview(); return; }
    stopPreview();
    if (!previewAudioRef.current) previewAudioRef.current = new Audio();
    const a = previewAudioRef.current;
    const token = getAuthToken();
    const sep = track.file_url.includes('?') ? '&' : '?';
    a.src = token
      ? `${FILE_BASE}${track.file_url}${sep}auth=${encodeURIComponent(token)}`
      : `${FILE_BASE}${track.file_url}`;
    a.onended = () => setPreviewTrackId(null);
    a.onerror = () => {
      setPreviewTrackId(null);
      toast.error(`"${track.title}" cannot be played — this is why kiosks skip it. Run "Check tracks" for the exact reason, then use Re-auth to fix it.`);
    };
    a.play().then(() => setPreviewTrackId(track.id)).catch(() => {
      setPreviewTrackId(null);
      toast.error(`"${track.title}" failed to play. Run "Check tracks" to see why.`);
    });
  };

  // ── Verify all Drive tracks (same probes the kiosk stream makes) ───────
  const handleVerify = async () => {
    setVerifying(true);
    try {
      const res = await apiClient.post('/tracks/verify');
      const { ok = 0, broken = 0 } = res.data || {};
      if (broken === 0) toast.success(`All ${ok} Drive track(s) are playable.`);
      else toast.warning(`${broken} of ${ok + broken} track(s) are NOT playable — see the red Health badges. Use Re-auth to fix them.`);
      await fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVerifying(false); }
  };

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;

  const pickerNotConfigured = driveStatus.connected && !driveConfig.configured;

  return (
    <div className="space-y-6" data-testid="music-library-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="library-title">Music Library</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">
            {driveStatus.connected ? 'Connected to Google Drive' : 'Connect Google Drive to add tracks'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {driveStatus.connected ? (
            <>
              <Button
                onClick={handleVerify}
                disabled={verifying}
                className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] disabled:opacity-50"
                data-testid="verify-tracks-button"
                title="Check every Drive track for playability — finds tracks kiosks would skip"
              >
                <ShieldCheck size={18} className="mr-2" />
                {verifying ? 'Checking…' : 'Check tracks'}
              </Button>
              <Button
                onClick={openImportPicker}
                disabled={pickerBusy || pickerNotConfigured}
                className="bg-blue-600 hover:bg-blue-700 text-white border-0 disabled:opacity-50"
                data-testid="pick-from-drive-button"
              >
                <Folder size={18} className="mr-2" />
                {pickerBusy ? 'Adding…' : 'Pick from Drive'}
              </Button>
              <Button onClick={handleDisconnectDrive} className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#6b665c] border border-[#c5d4c8]" data-testid="disconnect-drive-button">
                <LinkBreak size={16} />
              </Button>
            </>
          ) : (
            <Button onClick={handleConnectDrive} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="connect-drive-button">
              Connect Google Drive
            </Button>
          )}
        </div>
      </div>

      {pickerNotConfigured && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-4 flex items-start gap-3" data-testid="picker-not-configured-banner">
          <Warning size={20} weight="bold" className="mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="text-sm">
            <p className="font-semibold">Google Picker is not configured yet.</p>
            <p className="mt-1">
              Ask your MusicSync administrator to set <code className="bg-white/60 px-1 rounded">GOOGLE_API_KEY</code> in the backend environment.
              Streaming existing tracks still works — only adding new ones is blocked until the key is set.
            </p>
          </div>
        </div>
      )}

      {!driveStatus.connected && (
        <div className="bg-white border border-[#c5d4c8] rounded-md p-8 text-center" data-testid="drive-cta">
          <Folder size={48} className="text-[#a39e92] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-[#2d2a26] mb-2">Connect Google Drive to get started</h3>
          <p className="text-sm text-[#6b665c] max-w-md mx-auto mb-4">
            All music tracks are sourced from your Google Drive. Once connected, click <strong>Pick from Drive</strong> to
            open Google's file picker and choose songs to import — MusicSync only ever sees the files you explicitly pick.
          </p>
          <Button onClick={handleConnectDrive} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="connect-drive-cta">
            Connect Google Drive
          </Button>
        </div>
      )}

      {/* Tracks Table */}
      {tracks.length > 0 && (
        <div className="bg-white border border-[#c5d4c8] rounded-md overflow-hidden">
          <table className="w-full" data-testid="tracks-table">
            <thead className="bg-[#f5f0e8] border-b border-[#c5d4c8]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Title</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Artist</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Source</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Health</th>
                <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-[#6b665c]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {tracks.map(track => (
                <tr key={track.id} className="hover:bg-[#f5f0e8] transition-all" data-testid={`track-row-${track.id}`}>
                  <td className="px-6 py-4 font-medium">{track.title}</td>
                  <td className="px-6 py-4 text-[#6b665c] font-mono text-sm">{track.artist}</td>
                  <td className="px-6 py-4">
                    {track.drive_file_id ? (
                      <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-blue-900/30 text-[#7c9082] border border-[#c5d4c8]">Drive</span>
                    ) : (
                      <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-[#e4ebe5] text-[#8a8477]">Legacy</span>
                    )}
                  </td>
                  <td className="px-6 py-4" data-testid={`track-health-${track.id}`}>
                    {track.verify_status ? (
                      track.verify_status === 'ok' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <ShieldCheck size={12} /> Playable
                        </span>
                      ) : (
                        <span
                          title={track.verify_detail}
                          className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 cursor-help"
                        >
                          <WarningCircle size={12} /> {String(track.verify_status).replace(/_/g, ' ')}
                        </span>
                      )
                    ) : (
                      <span className="text-xs font-mono text-[#a39e92]">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handlePreview(track)}
                        className={previewTrackId === track.id
                          ? 'bg-[#2d2a26] hover:bg-black text-white border-0 h-8 px-2'
                          : 'bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] h-8 px-2'}
                        data-testid={`preview-track-${track.id}`}
                        title={previewTrackId === track.id ? 'Stop preview' : 'Preview this track (same stream kiosks use)'}
                      >
                        {previewTrackId === track.id ? <Stop size={16} weight="fill" /> : <Play size={16} />}
                      </Button>
                      {track.drive_file_id && driveStatus.connected && (
                        <Button
                          onClick={() => openReauthPicker(track.id)}
                          disabled={pickerBusy || pickerNotConfigured}
                          className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] text-xs h-8 px-2 disabled:opacity-50"
                          data-testid={`reauth-track-${track.id}`}
                          title="Re-authorise this track by re-picking it from Google Drive"
                        >
                          <ArrowsClockwise size={14} className="mr-1" />
                          Re-auth
                        </Button>
                      )}
                      <Button onClick={() => handleDelete(track.id)} className="bg-[#fce8eb] hover:bg-[#f0c4cb] text-[#b5636e] border-0" data-testid={`delete-track-${track.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {playbackErrors.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-md overflow-hidden" data-testid="playback-errors-panel">
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
            <Warning size={16} className="text-amber-600" />
            <h3 className="text-sm font-bold text-amber-900">Recently skipped on kiosks</h3>
            <span className="text-xs font-mono text-amber-700">({playbackErrors.length} event{playbackErrors.length === 1 ? '' : 's'})</span>
          </div>
          <div className="divide-y divide-[#f0ead9] max-h-64 overflow-y-auto">
            {playbackErrors.map((pe) => (
              <div key={pe.id} className="px-6 py-2 flex items-center justify-between text-sm gap-4" data-testid={`playback-error-${pe.id}`}>
                <span className="font-medium truncate">{pe.title}</span>
                <span className="text-xs font-mono text-[#8a8477] flex-shrink-0 text-right">
                  {pe.store_name || pe.store_id} · {pe.reason} · {new Date(pe.at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tracks.length === 0 && driveStatus.connected && (
        <div className="bg-white border border-[#c5d4c8] rounded-md text-center py-12">
          <MusicNotes size={48} className="text-[#a39e92] mx-auto mb-4" />
          <p className="text-[#8a8477] font-mono">No tracks yet. Click <strong>Pick from Drive</strong> to import songs.</p>
        </div>
      )}
    </div>
  );
};

export default MusicLibrary;
